/**
 * useBreakout — manages breakout rooms lifecycle.
 *
 * Extracted from VisioRoom.tsx (M004-breakout cycle 2).
 * Handles: create/recall API calls, DataChannel broadcast + receive,
 * timer countdown, state recovery on rejoin.
 */

import { useState, useEffect, useCallback } from 'react'
import type { Room, LocalParticipant } from 'livekit-client'
import { RoomEvent } from 'livekit-client'
import { apiFetch } from '../lib/apiFetch'
import type { BreakoutAssignMessage, BreakoutRecallMessage } from './visioDataTypes'
import { parseDataMessage } from './visioDataTypes'

interface UseBreakoutParams {
  roomId?: string
  isAdmin: boolean
  room: Room
  localParticipant: LocalParticipant
  setActiveToken: (t: string) => void
  setActiveServerUrl: (u: string) => void
  logEvt: (eventType: string, eventData?: Record<string, unknown>) => void
}

interface UseBreakoutReturn {
  breakoutActive: boolean
  breakoutRoomN: number | null
  timerSec: number | null
  showBreakoutSetup: boolean
  setShowBreakoutSetup: (v: boolean) => void
  startBreakout: (count: number, assignments: Record<string, number>, timerMin?: number) => void
  recallBreakout: () => void
}

export function useBreakout({
  roomId, isAdmin, room, localParticipant,
  setActiveToken, setActiveServerUrl, logEvt,
}: UseBreakoutParams): UseBreakoutReturn {

  const [breakoutActive, setBreakoutActive] = useState(false)
  const [breakoutRoomN, setBreakoutRoomN] = useState<number | null>(null)
  const [timerSec, setTimerSec] = useState<number | null>(null)
  const [showBreakoutSetup, setShowBreakoutSetup] = useState(false)

  // -- Apply breakout assign (reused by startBreakout + DataChannel receive) --

  const applyAssign = useCallback((
    tokens: Record<string, string>,
    url: string,
    assignments: Record<string, number>,
    timerMinutes?: number,
    startedAt?: number,
  ) => {
    const myToken = tokens[localParticipant.identity]
    if (!myToken) return
    setActiveToken(myToken)
    setActiveServerUrl(url)
    setBreakoutActive(true)
    setBreakoutRoomN(assignments[localParticipant.identity] ?? 1)
    if (timerMinutes && startedAt) {
      const elapsed = Date.now() / 1000 - startedAt
      const remaining = Math.max(0, Math.floor(timerMinutes * 60 - elapsed))
      if (remaining > 0) setTimerSec(remaining)
    } else if (timerMinutes) {
      setTimerSec(timerMinutes * 60)
    }
  }, [localParticipant.identity, setActiveToken, setActiveServerUrl])

  // -- Apply recall (reused by recallBreakout + DataChannel receive) ----------

  const applyRecall = useCallback((tokens: Record<string, string>, url: string) => {
    const myToken = tokens[localParticipant.identity]
    if (!myToken) return
    setActiveToken(myToken)
    setActiveServerUrl(url)
    setBreakoutActive(false)
    setBreakoutRoomN(null)
    setTimerSec(null)
  }, [localParticipant.identity, setActiveToken, setActiveServerUrl])

  // -- Start breakout (admin action) ------------------------------------------

  const startBreakout = useCallback(async (
    count: number,
    assignments: Record<string, number>,
    timerMin?: number,
  ) => {
    if (!roomId) return
    try {
      const res = await apiFetch(`/api/visio/rooms/${roomId}/breakout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ count, assignments, timer_minutes: timerMin }),
      })
      if (!res.ok) { console.warn('[useBreakout] startBreakout', res.status); return }
      const data = await res.json()
      applyAssign(data.tokens, data.url, assignments, timerMin, data.started_at)
      logEvt('breakout.created', { count, timer_minutes: timerMin })
      // Broadcast to all participants via DataChannel
      try {
        const msg: BreakoutAssignMessage = {
          type: 'breakout_assign',
          tokens: data.tokens,
          url: data.url,
          assignments,
          timer_minutes: timerMin,
          started_at: data.started_at,
        }
        const encoder = new TextEncoder()
        room.localParticipant.publishData(encoder.encode(JSON.stringify(msg)), { reliable: true })
      } catch (e) { console.warn('[useBreakout] broadcast assign', e) }
    } catch (e) { console.warn('[useBreakout] startBreakout', e) }
    setShowBreakoutSetup(false)
  }, [roomId, room, applyAssign, logEvt])

  // -- Recall breakout (admin action) -----------------------------------------

  const recallBreakout = useCallback(async () => {
    if (!roomId) return
    try {
      const res = await apiFetch(`/api/visio/rooms/${roomId}/breakout/recall`, { method: 'POST' })
      if (!res.ok) { console.warn('[useBreakout] recallBreakout', res.status); return }
      const data = await res.json()
      applyRecall(data.tokens, data.url)
      logEvt('breakout.recalled')
      // Broadcast recall via DataChannel
      try {
        const msg: BreakoutRecallMessage = {
          type: 'breakout_recall',
          tokens: data.tokens,
          url: data.url,
        }
        const encoder = new TextEncoder()
        room.localParticipant.publishData(encoder.encode(JSON.stringify(msg)), { reliable: true })
      } catch (e) { console.warn('[useBreakout] broadcast recall', e) }
    } catch (e) { console.warn('[useBreakout] recallBreakout', e) }
  }, [roomId, room, applyRecall, logEvt])

  // -- DataChannel listener for breakout messages -----------------------------

  useEffect(() => {
    if (!room) return
    const handler = (payload: Uint8Array) => {
      const data = parseDataMessage(payload)
      if (!data) return
      if (data.type === 'breakout_assign') {
        applyAssign(data.tokens, data.url, data.assignments, data.timer_minutes, data.started_at)
      }
      if (data.type === 'breakout_recall') {
        applyRecall(data.tokens, data.url)
      }
    }
    room.on(RoomEvent.DataReceived, handler)
    return () => { room.off(RoomEvent.DataReceived, handler) }
  }, [room, applyAssign, applyRecall])

  // -- Recover breakout state from metadata on mount/rejoin -------------------

  useEffect(() => {
    if (!roomId) return
    const fetchBreakoutState = async () => {
      try {
        const res = await apiFetch(`/api/visio/rooms/${roomId}`)
        if (!res.ok) return
        const roomData = await res.json()
        const meta = roomData.metadata || {}
        const breakout = typeof meta === 'string' ? JSON.parse(meta)?.breakout : meta?.breakout
        if (breakout?.active && breakout.timer_minutes && breakout.started_at) {
          const elapsed = Date.now() / 1000 - breakout.started_at
          const remaining = Math.max(0, Math.floor(breakout.timer_minutes * 60 - elapsed))
          if (remaining > 0) {
            setTimerSec(remaining)
            setBreakoutActive(true)
            const myAssignment = breakout.assignments?.[localParticipant.identity]
            if (myAssignment) setBreakoutRoomN(myAssignment)
          }
        }
      } catch (e) { console.warn('[useBreakout] fetchBreakoutState', e) }
    }
    fetchBreakoutState()
  }, [roomId]) // eslint-disable-line react-hooks/exhaustive-deps

  // -- Timer countdown --------------------------------------------------------

  useEffect(() => {
    if (timerSec === null || timerSec <= 0) return
    const interval = setInterval(() => {
      setTimerSec(prev => {
        if (prev === null || prev <= 1) {
          clearInterval(interval)
          if (isAdmin) recallBreakout()
          return null
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [timerSec !== null]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    breakoutActive,
    breakoutRoomN,
    timerSec,
    showBreakoutSetup,
    setShowBreakoutSetup,
    startBreakout,
    recallBreakout,
  }
}
